import React, { useEffect, useRef, useState } from 'react';
import {
  EliceAudioPlayer,
  EliceButton,
  ReadyRecord,
  Recording,
  RecordPause,
  RecordPlay,
} from '@elicecontents/content-ui';
import { Button, Stack, styled, Typography } from '@mui/material';

import { SoundVisualizer } from '../sound-visualizer';
import { type AudioStep, useRecorder } from './RecorderContext';

import type { SoundVisualizerVariant } from '@elicecontents/content-ui';
import type { SxProps } from '@mui/material';

export type RecorderStep = 'ready' | 'recording' | 'pause' | 'waiting';
export type RecorderType = 'needPause' | 'needWaiting' | 'allNeed' | 'notNeed';

export interface EliceRecorderStepProps {
  onAudioReady: (file: File) => void;
  onTransform?: () => void;
  type?: RecorderType;
  visualType?: SoundVisualizerVariant;
  isReadyVisual?: boolean;
  onRecord?: () => void;
  onResume?: () => void;
  onPause?: () => void;
  defaultBarColor?: string;
  closeRecorder?: () => void;
  onTranscribingChange?: (value: boolean) => void;
  isLoadingMessage?: boolean;
  forcedStep?: AudioStep;
  sx?: SxProps;
  onRecordStep?: (step: AudioStep) => void;
  initFile?: File;
  disabled?: boolean;
  isNotAudioPlayer?: boolean;
  isNotRecordingPlayer?: boolean;
  notNeedSubmit?: boolean;
}

const EliceRecorderStep = ({
  onAudioReady,
  onTransform,
  visualType = 'bar',
  onRecord,
  type = 'notNeed',
  isReadyVisual = false,
  onResume,
  onPause,
  onTranscribingChange,
  forcedStep,
  closeRecorder,
  defaultBarColor,
  sx,
  onRecordStep,
  initFile,
  disabled = false,
  isNotAudioPlayer = false,
  isNotRecordingPlayer = false,
  notNeedSubmit = false,
}: EliceRecorderStepProps) => {
  const {
    state: recorderState,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    setIsRecording,
    setStep,
  } = useRecorder();
  const [audioUrl, setAudioUrl] = useState<string>('');
  const currentStep = forcedStep ?? recorderState.step;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handleStart = async () => {
    onTranscribingChange?.(true);
    onRecord?.();

    await startRecording(async file => {
      const arrayBuffer = await file.arrayBuffer();
      const audioContext = new AudioContext();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const wavBuffer = audioBufferToWav(audioBuffer);
      const wavFile = new Blob([wavBuffer], { type: 'audio/wav' });
      const wavFileWithName = new File([wavFile], 'recorded_audio.wav', {
        type: 'audio/wav',
      });
      onAudioReady(wavFileWithName);
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
    });
  };

  const firstTimeInit = React.useRef(false);

  React.useEffect(() => {
    if (initFile && !firstTimeInit.current) {
      const url = URL.createObjectURL(initFile);
      setAudioUrl(url);
      setStep('waiting');
      firstTimeInit.current = true;
    }

    if (!initFile) {
      setStep('ready');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initFile]);

  const handlePause = () => {
    onPause?.();
    pauseRecording();
  };

  const handleResume = () => {
    onResume?.();
    resumeRecording();
  };

  const handleStop = () => {
    stopRecording(false);
    onTranscribingChange?.(false);
  };

  const handleSubmit = () => {
    onTransform?.();
    closeRecorder?.();
    stopRecording(true);
    setIsRecording(false);
  };

  const handleOnlySubmit = () => {
    stopRecording(true);
    onTranscribingChange?.(false);
    onTransform?.();
    closeRecorder?.();
    setIsRecording(false);
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.currentTime = 0;
        audioRef.current
          .play()
          .catch(e => console.error('Audio play failed', e));
        setIsPlaying(true);
      }
    }
  };

  useEffect(() => {
    if (isNotAudioPlayer && audioUrl) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => {
        setIsPlaying(false);
        if (audioRef.current) audioRef.current.currentTime = 0;
      };
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioUrl, isNotAudioPlayer]);

  useEffect(() => {
    onRecordStep?.(currentStep);
  }, [currentStep, onRecordStep]);

  if (!currentStep) {
    return <SkeletonVisualizerBox sx={sx} />;
  }
  return (
    <StyledVisualizerBox sx={sx ? (currentStep !== 'ready' ? sx : {}) : {}}>
      {currentStep === 'ready' && (
        <>
          {!isReadyVisual && (
            <SoundVisualizer
              analyser={recorderState.analyser}
              variant={visualType}
              bgColor={'transparnet'}
              defaultBarColor={defaultBarColor}
            />
          )}
          <StyledRecorderButton onClick={handleStart} step={currentStep}>
            <ReadyRecord />
            <Typography variant='subtitle2'>녹음 시작</Typography>
          </StyledRecorderButton>
        </>
      )}
      {currentStep === 'recording' && (
        <>
          {!isNotRecordingPlayer && (
            <SoundVisualizer
              analyser={recorderState.analyser}
              variant={visualType}
              defaultBarColor={defaultBarColor}
              bgColor={'transparnet'}
            />
          )}
          <StyledVisualizerBoxButtonContainer>
            {(type === 'needPause' || type === 'allNeed') && (
              <StyledPauseButton
                onClick={handlePause}
                recorderType={type}
                isRecording={currentStep === 'recording'}
              >
                <Recording color="#485EAD" squareColor="#0A379B" />
                <Typography variant="subtitle2">일시 정지</Typography>
              </StyledPauseButton>
            )}
            <StyledRecorderButton
              onClick={
                type === 'needPause' ||
                  type === 'allNeed' ||
                  type === 'needWaiting'
                  ? handleStop
                  : handleOnlySubmit
              }
              recorderType={type}
              step={currentStep}
            >
              <Recording />
              <Typography variant="subtitle2">녹음 완료</Typography>
            </StyledRecorderButton>
          </StyledVisualizerBoxButtonContainer>
        </>
      )}
      {currentStep === 'pause' && (
        <>
          {!isNotAudioPlayer && (
            <SoundVisualizer
              analyser={null}
              variant={visualType}
              bgColor={'transparnet'}
            />
          )}
          <StyledVisualizerBoxButtonContainer>
            {(type === 'needPause' || type === 'allNeed') && (
              <StyledPauseButton onClick={handleResume} recorderType={type}>
                <Recording color="#485EAD" squareColor="#0A379B" />
                <Typography variant="subtitle2">이어서 녹음</Typography>
              </StyledPauseButton>
            )}
            <StyledRecorderButton
              onClick={handleStop}
              step={currentStep}
              recorderType={type}
            >
              <Recording />
              <Typography variant="subtitle2">녹음 완료</Typography>
            </StyledRecorderButton>
          </StyledVisualizerBoxButtonContainer>
        </>
      )}
      {currentStep === 'waiting' &&
        (type === 'needWaiting' || type === 'allNeed') && (
          <>
            {isNotAudioPlayer ? (
              <StyledVisualizerBoxButtonContainer>
                <StyledPauseButton
                  onClick={handlePlayPause}
                  recorderType={type}
                >
                  {isPlaying ? <RecordPause /> : <RecordPlay />}
                  <Typography variant="subtitle2">{isPlaying ? '정지' : '재생'}</Typography>
                </StyledPauseButton>
                <StyledRecorderButton
                  disabled={disabled}
                  onClick={handleStart}
                  step={currentStep}
                >
                  <ReadyRecord />
                  <Typography variant="subtitle2">다시 녹음</Typography>
                </StyledRecorderButton>
                {!notNeedSubmit && (
                  <EliceButton disabled={disabled} onClick={handleSubmit}>
                    <Typography variant="subtitle2">완료</Typography>
                  </EliceButton>
                )}
              </StyledVisualizerBoxButtonContainer>
            ) : (
              <>
                {audioUrl && <EliceAudioPlayer src={audioUrl} />}
                <StyledVisualizerBoxButtonContainer>
                  <StyledRecorderButton
                    disabled={disabled}
                    onClick={handleStart}
                    step={currentStep}
                  >
                    <ReadyRecord />
                    <Typography variant="subtitle2">다시 녹음</Typography>
                  </StyledRecorderButton>
                  {!notNeedSubmit && (
                    <EliceButton disabled={disabled} onClick={handleSubmit}>
                      <Typography variant="subtitle2">완료</Typography>
                    </EliceButton>
                  )}
                </StyledVisualizerBoxButtonContainer>
              </>
            )}
          </>
        )}
    </StyledVisualizerBox>
  );
};

export default EliceRecorderStep;

const StyledVisualizerBox = styled(Stack)`
  width: 100%;
  font-size: 20px;
  background-color: transparent;
  border-radius: 1.2em;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1.6em;
`;
const SkeletonVisualizerBox = styled(Stack)`
  width: 100%;
  font-size: 20px;
  height: 10.6em;
  border-radius: 1.2em;
  background: linear-gradient(
    90deg,
    #e0e0e0 25%,
    #f5f5f5 37%,
    #f5f5f5 63%,
    #e0e0e0 75%
  );
  background-size: 400% 100%;
  animation: skeleton-loading 1.5s ease-in-out infinite;

  @keyframes skeleton-loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: -100% 50%;
    }
  }
`;

const StyledVisualizerBoxButtonContainer = styled(Stack)`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 1.2em;
  justify-content: center;
  align-items: center;
`;

const StyledRecorderButtonDefault = styled(Button)`
  display: flex;
  border: none;
  height: 46px;
  padding: 12px 20px 12px 17px;
  justify-content: center;
  align-items: center;
  gap: 0.4em;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 16px;
`;

const StyledRecorderButton = styled(StyledRecorderButtonDefault) <{
  step: RecorderStep;
  recorderType?: RecorderType | undefined;
}>`
  width: ${({ recorderType }) =>
    recorderType === 'needPause' || recorderType === 'allNeed'
      ? '155px'
      : '155px'};
  color: ${({ step }) =>
    step === 'ready' || step === 'waiting' ? '#fff' : '#FF5D58'};
  background: ${({ step }) =>
    step === 'ready' || step === 'waiting' ? '#FF5D58' : '#FBE0E0'};

  &:hover {
    background: #FFB1AF !important;
    color: #FF5D58 !important;
  }
`;

const StyledPauseButton = styled(StyledRecorderButtonDefault) <{
  recorderType?: RecorderType | undefined;
  isRecording?: boolean;
}>`
  width: ${({ recorderType }) =>
    recorderType === 'needPause' || recorderType === 'allNeed'
      ? '155px'
      : '155px'};
  color: #0a379b;
  background: #bec4e1;

  @keyframes blinker {
    50% {
      background-color: #0a379b;
      color: #bec4e1;
    }
  }

  animation: ${({ isRecording }) =>
    isRecording ? 'blinker 1s linear infinite' : 'none'};

  &:hover {
    color: #bec4e1;
    background-color: #0a379b;
  }
`;

function audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
  const numOfChannels = buffer.numberOfChannels; // 채널 수 가져오기
  const sampleRate = buffer.sampleRate; // 샘플 레이트 가져오기
  const format = numOfChannels === 1 ? 1 : 2; // 포맷 설정 (1: mono, 2: stereo)
  // WAV 데이터의 총 바이트 길이 계산 (헤더 44바이트 포함)
  const byteLength = buffer.length * numOfChannels * 2 + 44; // 파일 전체 크기 (오디오 샘플의 총 수 * 오디오 채널 수 * 2(16bit-PCM) + 44(wav 헤더의 크기))
  const wavArrayBuffer = new ArrayBuffer(byteLength); // WAV 데이터 저장을 위한 ArrayBuffer 생성
  const wavView = new DataView(wavArrayBuffer); // ArrayBuffer를 다루기 위한 DataView 생성
  // WAV 헤더 작성
  let offset = 0; // 현재 오프셋 초기화
  const writeString = (str: string) => {
    // 문자열을 DataView에 쓰는 함수
    for (let i = 0; i < str.length; i++) {
      wavView.setUint8(offset + i, str.charCodeAt(i)); // 문자열의 각 문자에 대한 ASCII 값을 설정
    }
    offset += str.length; // 오프셋 업데이트
  };

  writeString('RIFF'); // Chunk ID : RIFF 헤더 시작
  wavView.setUint32(offset, byteLength - 8, true); // Chunk Size : 전체 데이터 크기 설정(전체 파일 크기 - Chunk ID, Chunk Size)
  offset += 4; // 오프셋 업데이트
  writeString('WAVE'); // Format

  writeString('fmt '); // fmt ChunkID : fmt 청크 시작
  wavView.setUint32(offset, 16, true); // Subchunk1Size (16바이트)
  offset += 4; // 오프셋 업데이트
  wavView.setUint16(offset, 1, true); // AudioFormat (1: PCM)
  offset += 2; // 오프셋 업데이트
  wavView.setUint16(offset, format, true); // NumChannels 설정
  offset += 2; // 오프셋 업데이트
  wavView.setUint32(offset, sampleRate, true); // SampleRate 설정
  offset += 4; // 오프셋 업데이트
  wavView.setUint32(offset, sampleRate * numOfChannels * 2, true); // ByteRate 설정
  offset += 4; // 오프셋 업데이트
  wavView.setUint16(offset, numOfChannels * 2, true); // BlockAlign 설정
  offset += 2; // 오프셋 업데이트
  wavView.setUint16(offset, 16, true); // BitsPerSample 설정
  offset += 2; // 오프셋 업데이트

  writeString('data'); // data 청크 시작
  wavView.setUint32(offset, byteLength - offset - 4, true); // Subchunk2Size 설정
  offset += 4; // 오프셋 업데이트
  // PCM 데이터 복사, Raw Data 삽입
  for (let channel = 0; channel < numOfChannels; channel++) {
    // 각 채널에 대해 반복
    const channelData = buffer.getChannelData(channel); // 해당 채널의 데이터 가져오기
    for (let i = 0; i < channelData.length; i++) {
      // 각 샘플에 대해 반복
      wavView.setInt16(
        // PCM 데이터를 WAV 포맷으로 변환하여 DataView에 설정
        44 + i * 2 * numOfChannels + channel * 2, // WAV 데이터 시작 위치 계산
        channelData[i] * 0x7fff, // 샘플 값을 16-bit PCM 형식으로 변환
        true // little-endian 형식
      );
    }
  }
  return wavArrayBuffer;
}
